import {
  CARD_OBJECT_NAME,
  _clearClientMsgIdCache,
  assertCardData as assertCardDataCore,
  buildClientMsgId as buildClientMsgIdCore,
  buildSendCardBody,
  normalizePresendMessage as normalizePresendMessageCore,
} from './core/send-card-body';
import { emit } from './events';
import { DAJI_MSG_SOURCE, DAJI_MSG_VERSION, getAllowedOrigins } from './launcher/bridge';
import { onReset } from './lifecycle';
import type { CardData, CouponCard, OpenOptions, OrderCard, PresendCardKind, ProductCard } from './types';
import { fetchWithRetry } from './utils/fetch-with-retry';

let apiBase: string | null = null;
let debug = false;

export function _setApiBase(base: string, debugMode: boolean): void {
  apiBase = base;
  debug = debugMode;
}

onReset(() => {
  apiBase = null;
  debug = false;
  _clearClientMsgIdCache();
});

/** 包装：在 throw 前 emit('error') 以便宿主统一监听 */
export function buildClientMsgId(scope: string, payload: unknown): string {
  return buildClientMsgIdCore(scope, payload);
}

function assertCardData(kind: PresendCardKind, data: unknown): asserts data is CardData {
  try {
    assertCardDataCore(kind, data);
  } catch (e) {
    if (debug) console.warn((e as Error).message, data);
    emit('error', { source: `validateCard:${kind}`, error: e as Error });
    throw e;
  }
}

export function normalizePresendMessage(opts: OpenOptions): { kind: PresendCardKind; data: CardData } | null {
  try {
    return normalizePresendMessageCore(opts);
  } catch (e) {
    if (debug) console.warn((e as Error).message, opts.message);
    emit('error', { source: 'normalizePresendMessage', error: e as Error });
    throw e;
  }
}

/** type-safe emit 包装：discriminated union 下 emit 必须分支处理 */
export function emitPresendStart(
  clientMsgId: string,
  presend: { kind: PresendCardKind; data: CardData },
  opts: OpenOptions,
): void {
  if (presend.kind === 'product') {
    emit('presend:start', { clientMsgId, kind: 'product', cardData: presend.data as ProductCard, opts });
    return;
  }
  if (presend.kind === 'order') {
    emit('presend:start', { clientMsgId, kind: 'order', cardData: presend.data as OrderCard, opts });
    return;
  }
  emit('presend:start', { clientMsgId, kind: 'coupon', cardData: presend.data as CouponCard, opts });
}

export interface PreSendCardInput {
  apiBase: string;
  groupId: string;
  customType: PresendCardKind;
  card: CardData;
  token?: string;
  tenant?: string;
  source?: string;
  debug?: boolean;
  /** 已合并 (boot.headers + open.headers) 的额外请求头 */
  extraHeaders?: Record<string, string>;
}

/**
 * 真正的预投：以访客身份向后端 `front/chat/sendMsg` 投递一条 `DAJI:Card` 消息。
 * groupId 必须是 `front/chat/open` 返回的 sessionId（融云群聊模式下二者等价）。
 * 入参校验失败抛 TypeError；运行时（fetch / 后端 code）失败抛普通 Error，由调用方决定兜底。
 */
export async function preSendCard(input: PreSendCardInput): Promise<void> {
  assertCardData(input.customType, input.card);
  const url = `${input.apiBase.replace(/\/$/, '')}/api/customer/front/chat/sendMsg`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(input.extraHeaders ?? {}),
  };
  if (input.token) {
    headers.accesstoken = String(input.token);
    headers.authorization = String(input.token);
  }
  if (input.tenant) headers['x-tenant-code'] = String(input.tenant);
  headers['x-auth-source'] = input.source || 'daji';

  const body = buildSendCardBody(input.groupId, input.customType, input.card);
  if (input.debug) console.log('[DajiCS][preSend] POST', url, body);

  const res = await fetchWithRetry(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    debug: input.debug,
  });
  let json: { code?: number; msg?: string; message?: string } | null = null;
  try {
    json = (await res.json()) as { code?: number; msg?: string; message?: string };
  } catch {
    return;
  }
  if (json && json.code !== undefined && json.code !== 200 && json.code !== 100) {
    throw new Error(json.msg || json.message || `预投失败: code=${json.code}`);
  }
}

export interface SendCardOptions {
  userId: string | number;
  supplierId: string | number;
  token?: string;
  /**
   * 已加载完成的客服窗口/iframe 句柄。**必填**：本方法只走 postMessage 路径，
   * 用于宿主自管 iframe 的场景。如需在新开窗前预投，请改用 `open({ message })`。
   */
  targetWindow: Window;
}

export interface SendCardResult {
  success: boolean;
  clientMsgId: string;
  error?: string;
}

export interface SendGenericCardPayload {
  kind: PresendCardKind;
  data: CardData;
}

/** 统一的失败返回：所有出错路径走这里，与文档承诺"出错不抛"一致。 */
function fail(error: string, clientMsgId = ''): SendCardResult {
  if (debug) console.warn('[DajiCS] sendCard failed:', error);
  return { success: false, clientMsgId, error };
}

/**
 * 公开 API：向已加载的客服窗口/iframe 主动追发一张卡片（商品/订单/优惠券）。
 *
 * 与 `open({ message })` 的区别：
 * - `open({ message })`：新开窗 *之前* 走后端 sendMsg 预投（用户尚未进入会话）
 * - `sendCard / sendProductCard / ...`：客服窗口已存在时走 postMessage（访客页 IM 直接发卡）
 *
 * **错误语义**：
 * - **业务参数错误**（必填字段缺失/为空、kind 非法、data 类型错乱）→ **抛 TypeError**
 *   程序员错误，开发期立刻发现；同时 emit('error') 便于统一监听。
 * - **运行时错误**（SDK 未 boot、targetWindow 缺失、targetOrigin 推断失败、超时、对端拒收）
 *   → 走 `SendCardResult { success: false, error }`，**不抛**。
 *
 * 调用方建议：开发期 try/catch 业务参数错误，生产环境根据 result.success 兜底。
 */
export async function sendCard(card: SendGenericCardPayload, options: SendCardOptions): Promise<SendCardResult> {
  // 入参严格校验：必填字段缺失/为空时抛 TypeError，强制宿主在开发期对齐
  if (!card || typeof card !== 'object' || !card.kind) {
    throw new TypeError('[DajiCS] sendCard: card.kind 必填（"product" | "order" | "coupon"）');
  }
  assertCardData(card.kind, card.data);

  if (!apiBase) {
    return fail('SDK 未初始化，请先调用 boot()');
  }
  if (!options?.targetWindow) {
    return fail('缺少 targetWindow；如需在开窗前预投，请改用 open({ message })');
  }

  const clientMsgId = buildClientMsgId('postmsg', {
    card,
    userId: options.userId,
    supplierId: options.supplierId,
  });

  const trustedOrigin = getAllowedOrigins()[0] ?? null;
  if (!trustedOrigin) {
    const msg = "无可信 targetOrigin（boot baseUrl/allowedOrigins 未配置）；已跳过避免向 '*' 广播敏感 payload";
    emit('error', { source: 'sendCard:targetOrigin', error: new Error(msg) });
    return fail(msg, clientMsgId);
  }

  if (debug) console.log('[DajiCS] sendCard via postMessage:', { card, clientMsgId, trustedOrigin });

  return new Promise<SendCardResult>((resolve) => {
    const timer = setTimeout(() => {
      window.removeEventListener('message', listener);
      resolve(fail('发送卡片超时', clientMsgId));
    }, 10_000);

    const listener = (event: MessageEvent) => {
      const data = event.data as {
        source?: string;
        type?: string;
        payload?: { clientMsgId?: string; success?: boolean; error?: string };
      } | null;
      if (
        data?.source === DAJI_MSG_SOURCE &&
        data?.type === 'daji:card-sent' &&
        data?.payload?.clientMsgId === clientMsgId
      ) {
        clearTimeout(timer);
        window.removeEventListener('message', listener);
        if (data.payload.success) {
          resolve({ success: true, clientMsgId });
        } else {
          resolve(fail(data.payload.error || '对端发送失败', clientMsgId));
        }
      }
    };
    window.addEventListener('message', listener);

    const message = {
      source: DAJI_MSG_SOURCE,
      version: DAJI_MSG_VERSION,
      type: 'daji:send-card',
      payload: {
        kind: card.kind,
        data: card.data,
        // 协议层兼容旧访客页 IM：仍带 card 字段（等价于 data）；
        // 这与 OpenOptions.card 入参兼容是两件事——后者已废弃，前者仍保留以兼容存量访客页。
        card: card.data,
        userId: options.userId,
        supplierId: options.supplierId,
        token: options.token,
        clientMsgId,
      },
    };
    options.targetWindow.postMessage(message, trustedOrigin);
  });
}

export async function sendProductCard(card: ProductCard, options: SendCardOptions): Promise<SendCardResult> {
  return sendCard({ kind: 'product', data: card }, options);
}

export async function sendOrderCard(card: OrderCard, options: SendCardOptions): Promise<SendCardResult> {
  return sendCard({ kind: 'order', data: card }, options);
}

export async function sendCouponCard(card: CouponCard, options: SendCardOptions): Promise<SendCardResult> {
  return sendCard({ kind: 'coupon', data: card }, options);
}
